import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserState } from './Context';
import Designation from './Designation';


const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div className="p-4">
          {children}
        </div>
      </div>
    </div>
  );
};

const HomeVideo = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [videos, setVideos] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [currentVideoId, setCurrentVideoId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const { user } = UserState();

  const fetchVideos = async () => {
    try {
      setLoading(true);
      console.log('Fetching videos from URL:', `${BASE_URL}getAllVideo`);
      const response = await axios.get(`${BASE_URL}getAllVideo`, {
        headers: {
          Authorization: `Bearer ${user.token}`
        }
      });

      console.log('Videos response:', response.data);
      if (response.data.success) {
        setVideos(response.data.data);
        console.log('Videos set in state:', response.data.data);
      } else {
        console.error('Failed to fetch videos:', response.data);
        setMessage({ text: 'Failed to fetch videos', type: 'error' });
      }
    } catch (err) {
      console.error('Error fetching videos:', err.response?.data || err.message);
      setMessage({ text: 'Error loading videos', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.token) {
      console.log("User token available, fetching videos");
      fetchVideos();
    } else {
      console.log("No user token available, skipping video fetch");
    }
  }, [user?.token]);

  const handleAddVideo = async (e) => {
    e.preventDefault();
    if (!videoUrl.trim()) {
      setMessage({ text: 'Please enter a valid URL', type: 'error' });
      return;
    }

    // Debug: Check if token exists
    console.log("Token available:", !!user?.token);
    console.log("Token value:", user?.token);
    console.log("BASE_URL:", BASE_URL);
    console.log("Adding video with data:", { videoUrl, thumbnailUrl });

    try {
      setActionLoading(true);
      const response = await axios.post(
        `${BASE_URL}addVideo`,
        { videoUrl, thumbnailUrl },
        {
          headers: {
            Authorization: `Bearer ${user.token}`
          }
        }
      );

      console.log("Add video response:", response.data);
      if (response.data.success) {
        setMessage({ text: 'Video added successfully!', type: 'success' });
        console.log("Calling fetchVideos after successful add");
        fetchVideos();
        setVideoUrl('');
        setThumbnailUrl('');
        setIsAddModalOpen(false);
      } else {
        console.error("Failed to add video:", response.data);
        setMessage({ text: response.data.message || 'Failed to add video', type: 'error' });
      }
    } catch (err) {
      console.error("Error adding video:", err.response?.data || err.message);
      setMessage({ text: 'Error adding video: ' + (err.response?.data?.message || err.message), type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateVideo = async (e) => {
    e.preventDefault();
    if (!videoUrl.trim() || !currentVideoId) {
      setMessage({ text: 'Please enter a valid URL', type: 'error' });
      return;
    }
  
    try {
      setActionLoading(true);
      const response = await axios.post(
        `${BASE_URL}updateVideo`,
        { videoUrl, thumbnailUrl, id: currentVideoId },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
  
      if (response.data.success) {
        setMessage({ text: 'Video updated successfully!', type: 'success' });
        fetchVideos();
        setVideoUrl('');
        setThumbnailUrl('');
        setIsUpdateModalOpen(false);
      } else {
        setMessage({ text: response.data.message || 'Failed to update video', type: 'error' });
      }
    } catch (err) {
      setMessage({ text: 'Error updating video', type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };


  const handleDeleteVideo = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
  
    try {
      setActionLoading(true);
      const response = await axios.post(
        `${BASE_URL}deleteVideo`,
        { id },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
  
      if (response.data.success) {
        setMessage({ text: 'Video deleted successfully!', type: 'success' });
        fetchVideos();
      } else {
        setMessage({ text: response.data.message || 'Failed to delete video', type: 'error' });
      }
    } catch (err) {
      setMessage({ text: 'Error deleting video', type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Open update modal with video details
  const openUpdateModal = (video) => {
    setCurrentVideoId(video._id);
    setVideoUrl(video.videoUrl);
    setThumbnailUrl(video.thumbnailUrl || '');
    setIsUpdateModalOpen(true);
  };

  // Clear message after a delay
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Video table component
  const VideoTable = () => (
    <div className="overflow-x-auto w-full">
      <div className="flex justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-800">Videos List</h2>
        <button
          onClick={() => {
            setVideoUrl('');
            setThumbnailUrl('');
            setIsAddModalOpen(true);
          }}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition flex items-center gap-2"
          disabled={actionLoading}
        >
          {actionLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
            </svg>
          )}
          Add Video
        </button>
      </div>
      
      {videos.length > 0 ? (
        <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Thumbnail</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Video URL</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Added</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {videos.map((video) => (
              <tr key={video._id} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap">
                  <img 
                    src={video.thumbnailUrl} 
                    alt="Video thumbnail" 
                    className="h-16 w-24 object-cover rounded-md" 
                  />
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="max-w-xs truncate text-sm text-gray-700">{video.videoUrl}</div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="text-sm text-gray-500">
                    {new Date(video.createdAt).toLocaleDateString()}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                  <div className="flex gap-2">
                    <button
                      onClick={() => openUpdateModal(video)}
                      className="text-indigo-600 hover:text-indigo-900 bg-indigo-100 hover:bg-indigo-200 p-2 rounded-md transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteVideo(video._id)}
                      className="text-red-600 hover:text-red-900 bg-red-100 hover:bg-red-200 p-2 rounded-md transition-colors"
                      disabled={actionLoading}
                    >
                      {actionLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-red-600"></div>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No videos available</p>
        </div>
      )}
    </div>
  );

  const tabs = [
    {
      title: "Featured Videos",
      content: <VideoTable />
    },
    {
      title: "Designation",
      content: <Designation/>
    }
  ];

  return (
    <>
    <div className="container mx-auto bg-white rounded-lg shadow-lg overflow-hidden p-4">
      {message.text && (
        <div className={`mb-4 p-3 rounded-md ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {message.text}
        </div>
      )}
      
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {tabs.map((tab, index) => (
          <button
            key={index}
            onClick={() => setActiveTab(index)}
            className={`px-6 py-3 rounded-full font-medium text-sm transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-1 ${
              activeTab === index
                ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {tab.title}
          </button>
        ))}
      </div>
      
      <div className="tab-content bg-gray-50 rounded-xl p-6 min-h-48 shadow-inner">
        {loading ? (
          <div className="flex justify-center items-center h-48">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          tabs[activeTab].content
        )}
      </div>

      
    </div>
    <Modal 
        isOpen={isAddModalOpen} 
        onClose={() => setIsAddModalOpen(false)}
        title="Add New Video"
      >
        <form onSubmit={handleAddVideo}>
          <div className="mb-4">
            <label htmlFor="videoUrl" className="block text-gray-700 mb-2">Video URL</label>
            <input
              type="url"
              id="videoUrl"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://youtube.com/..."
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="thumbnailUrl" className="block text-gray-700 mb-2">Thumbnail URL (Optional)</label>
            <input
              type="url"
              id="thumbnailUrl"
              value={thumbnailUrl}
              onChange={(e) => setThumbnailUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://example.com/thumbnail.jpg"
            />
            {thumbnailUrl && (
              <div className="mt-2">
                <p className="text-xs text-gray-500 mb-1">Preview:</p>
                <img 
                  src={thumbnailUrl} 
                  alt="Thumbnail preview" 
                  className="h-20 object-cover rounded-md border border-gray-200"
                  onError={(e) => {e.target.onerror = null; e.target.src = 'https://via.placeholder.com/320x180?text=Invalid+URL'}}
                />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setIsAddModalOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center gap-2"
              disabled={actionLoading}
            >
              {actionLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
              )}
              Add Video
            </button>
          </div>
        </form>
      </Modal>

      <Modal 
        isOpen={isUpdateModalOpen} 
        onClose={() => setIsUpdateModalOpen(false)}
        title="Update Video"
      >
        <form onSubmit={handleUpdateVideo}>
          <div className="mb-4">
            <label htmlFor="updateVideoUrl" className="block text-gray-700 mb-2">Video URL</label>
            <input
              type="url"
              id="updateVideoUrl"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://youtube.com/..."
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="updateThumbnailUrl" className="block text-gray-700 mb-2">Thumbnail URL (Optional)</label>
            <input
              type="url"
              id="updateThumbnailUrl"
              value={thumbnailUrl}
              onChange={(e) => setThumbnailUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://example.com/thumbnail.jpg"
            />
            {thumbnailUrl && (
              <div className="mt-2">
                <p className="text-xs text-gray-500 mb-1">Preview:</p>
                <img 
                  src={thumbnailUrl} 
                  alt="Thumbnail preview" 
                  className="h-20 object-cover rounded-md border border-gray-200"
                  onError={(e) => {e.target.onerror = null; e.target.src = 'https://via.placeholder.com/320x180?text=Invalid+URL'}}
                />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setIsUpdateModalOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center gap-2"
              disabled={actionLoading}
            >
              {actionLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
              )}
              Update
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default HomeVideo;